import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { trpc } from "@/lib/trpc";
import { Download, Printer, QrCode } from "lucide-react";
import { useLocation } from "wouter";
import { useEffect, useState } from "react";
import { toast } from "sonner";

export default function QRCodeManagement() {
  const [, navigate] = useLocation();
  const [selectedWellId, setSelectedWellId] = useState<number | null>(null);

  // Keep last successful QR set so the UI doesn't go blank during refetch/loading
  const [stableQrSet, setStableQrSet] = useState<any>(null);

  // If the selected well changes, clear the last QR set so we never render stale codes
  // while a new request is in-flight.
  useEffect(() => {
    setStableQrSet(null);
  }, [selectedWellId]);

  const { data: wells, isLoading: wellsLoading } = trpc.wells.list.useQuery({ activeOnly: true });
  const { data: qrSet, isLoading: qrLoading } = trpc.system.generateWellQRSet.useQuery(
    { wellId: selectedWellId! },
    {
      enabled: selectedWellId !== null,
      onSuccess: (data) => setStableQrSet(data),
      onError: (error: any) => {
        toast.error(error?.message || 'Failed to generate QR codes');
      },
    }
  );

  const qrSetToRender = qrSet || stableQrSet;

  const handlePrint = () => {
    window.print();
  };

  const handleDownloadAll = () => {
    if (!qrSetToRender) return;

    // Create a simple HTML page with all QR codes
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error('Please allow popups to download QR codes');
      return;
    }

    try {
      const html = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>${qrSetToRender.wellName} - QR Codes</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              padding: 20px;
              max-width: 800px;
              margin: 0 auto;
            }
            h1 {
              text-align: center;
              margin-bottom: 30px;
            }
            .qr-grid {
              display: grid;
              grid-template-columns: repeat(2, 1fr);
              gap: 30px;
              margin-bottom: 40px;
            }
            .qr-item {
              border: 2px solid #e5e7eb;
              border-radius: 8px;
              padding: 20px;
              text-align: center;
              page-break-inside: avoid;
            }
            .qr-item img {
              width: 250px;
              height: 250px;
              margin: 10px auto;
            }
            .qr-label {
              font-size: 18px;
              font-weight: bold;
              margin-bottom: 10px;
            }
            .qr-well {
              font-size: 14px;
              color: #666;
              margin-bottom: 15px;
            }
            @media print {
              body { padding: 10px; }
              .qr-grid { gap: 20px; }
            }
          </style>
        </head>
        <body>
          <h1>${qrSetToRender.wellName} - QR Codes</h1>
          <div class="qr-grid">
            ${qrSetToRender.qrCodes.map(qr => `
              <div class="qr-item">
                <div class="qr-label">${qr.label}</div>
                <div class="qr-well">${qrSetToRender.wellName}</div>
                <img src="${qr.dataUrl}" alt="${qr.label} QR Code" />
              </div>
            `).join('')}
          </div>
        </body>
      </html>
    `;

      printWindow.document.write(html);
      printWindow.document.close();
    } catch (error) {
      console.error('QR download failed:', error);
      toast.error('Failed to generate QR download');
    }
  };

  return (
    <div className="min-h-screen bg-background p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" onClick={() => navigate("/")}>
            ← Back to Dashboard
          </Button>
          <div>
            <h1 className="text-3xl font-bold">QR Code Management</h1>
            <p className="text-muted-foreground mt-1">
              Generate printable QR codes for well logging
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Wells List */}
          <Card className="lg:col-span-1">
            <CardHeader>
              <CardTitle>Select Well</CardTitle>
              <CardDescription>Choose a well to generate QR codes</CardDescription>
            </CardHeader>
            <CardContent>
              {wellsLoading ? (
                <p className="text-sm text-muted-foreground">Loading wells...</p>
              ) : (
                <div className="space-y-2">
                  {wells?.map((well) => (
                    <Button
                      key={well.id}
                      variant={selectedWellId === well.id ? "default" : "outline"}
                      className="w-full justify-start"
                      onClick={() => setSelectedWellId(well.id)}
                    >
                      <QrCode className="h-4 w-4 mr-2" />
                      {well.wellName}
                    </Button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* QR Codes Display */}
          <Card className="lg:col-span-2">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>
                    {qrSetToRender ? `${qrSetToRender.wellName} QR Codes` : 'QR Codes'}
                  </CardTitle>
                  <CardDescription>
                    Scan these codes to quickly open today's log
                  </CardDescription>
                </div>
                {qrSetToRender && (
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={handlePrint}>
                      <Printer className="h-4 w-4 mr-2" />
                      Print
                    </Button>
                    <Button size="sm" onClick={handleDownloadAll}>
                      <Download className="h-4 w-4 mr-2" />
                      Download
                    </Button>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {!selectedWellId ? (
                <div className="text-center py-12 text-muted-foreground">
                  <QrCode className="h-16 w-16 mx-auto mb-4 opacity-20" />
                  <p>Select a well to generate QR codes</p>
                </div>
              ) : qrLoading ? (
                <div className="text-center py-12 text-muted-foreground">
                  <p>Loading QR codes...</p>
                </div>
              ) : !qrSetToRender || !Array.isArray(qrSetToRender.qrCodes) || qrSetToRender.qrCodes.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <p>No QR codes found for this well</p>
                </div>
              ) : (
                <div
                  key={selectedWellId}
                  className="grid grid-cols-1 md:grid-cols-2 gap-6"
                >
                  {qrSetToRender.qrCodes.map((qr, index) => (
                    <Card key={index} className="overflow-hidden">
                      <CardHeader className="pb-3">
                        <CardTitle className="text-lg">{qr.label}</CardTitle>
                        <CardDescription className="text-xs">
                          {qr.focusField ? `Auto-scrolls to ${qr.focusField}` : 'Opens general log form'}
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="flex flex-col items-center">
                        <img
                          src={qr.dataUrl}
                          alt={`${qr.label} QR Code`}
                          className="w-48 h-48 border-2 border-border rounded"
                        />
                        <p className="text-xs text-muted-foreground mt-3 text-center">
                          Scan to log {qrSetToRender.wellName}
                        </p>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <Card className="mt-6">
          <CardHeader>
            <CardTitle>How to Use QR Codes</CardTitle>
          </CardHeader>
          <CardContent className="prose prose-sm max-w-none">
            <ol className="space-y-2">
              <li>
                <strong>Select a well</strong> from the list on the left to generate its QR codes
              </li>
              <li>
                <strong>Print or download</strong> the QR code sheet using the buttons above
              </li>
              <li>
                <strong>Post QR codes</strong> near the corresponding equipment (hour meter, chlorine tank, etc.)
              </li>
              <li>
                <strong>Scan with a mobile device</strong> to instantly open today's log for that well
              </li>
              <li>
                <strong>Auto-scroll</strong> - Instrument-specific QR codes will automatically scroll to the relevant field
              </li>
            </ol>
            <div className="mt-4 p-4 bg-muted rounded-lg">
              <p className="text-sm font-semibold mb-2">💡 Pro Tips:</p>
              <ul className="text-sm space-y-1">
                <li>Laminate printed QR codes for outdoor use</li>
                <li>Use the "General Well Log" QR for main entry points</li>
                <li>Place instrument-specific QR codes directly on equipment</li>
                <li>QR codes work with any smartphone camera app</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
